import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SYSTEM_TABS } from "@shared/copy";
import { api } from "../lib/api";
import type { SystemRecordView } from "@shared/types";
import { MapView } from "../components/MapView";
import { LineageGraph } from "../components/LineageGraph";
import { AssumptionCard } from "../components/AssumptionCard";
import { Assistant } from "../components/Assistant";
import { StatusPill } from "../components/Status";

export function SystemRecord() {
  const { slug } = useParams();
  const [view, setView] = useState<SystemRecordView | null>(null);
  const [tab, setTab] = useState<(typeof SYSTEM_TABS)[number]>("Overview");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setErr(null);
    api<SystemRecordView>(`/api/systems/${slug}`)
      .then(setView)
      .catch((e) => setErr(String(e)));
  }, [slug]);

  if (err) return <section className="page-hero"><div className="wrap"><h1>Not found</h1><p>{err}</p></div></section>;
  if (!view) return <section className="page-hero"><div className="wrap"><p className="muted">Loading system record…</p></div></section>;
  const h = view.header;

  return (
    <>
      {(tab === "Map" || tab === "Overview") && (
        <MapView lat={h.lat} lng={h.lng} layers={view.layers} isDemo={view.isDemo} />
      )}
      <div className="wrap record-head">
        <p className="kicker">Flood System Record · {h.onenetworkId}</p>
        <h1>{h.name}</h1>
        <div className="record-meta">
          <span>{h.kind}</span>
          <span>{h.lga}</span>
          <span>{h.yearSpan}</span>
          <span>{h.studyCount} studies</span>
          <span>{h.modelCount} model versions</span>
          <span>{h.organisations} organisations</span>
          {view.isDemo ? <StatusPill status="DEMO / SYNTHETIC" /> : null}
        </div>
        <p className="summary">{h.summary}</p>
        <p>
          <Link className="btn" to={`/api/continuity/${h.slug}`} reloadDocument>
            Model continuity pack
          </Link>{" "}
          <Link className="btn btn-ghost" to={`/compare`}>
            Compare generations
          </Link>
        </p>
      </div>
      <div className="wrap">
        <div className="tabs">
          {SYSTEM_TABS.map((t) => (
            <button key={t} className={tab === t ? "active" : ""} type="button" onClick={() => setTab(t)}>
              {t}
            </button>
          ))}
        </div>
        <div className="panel">
          {tab === "Overview" && (
            <>
              <div className="grid-2">
                {view.intelligence.map((i) => (
                  <article className="card" key={i.id}>
                    <p className="kicker">{i.kind}</p>
                    <h3>{i.title}</h3>
                    <p>{i.statement}</p>
                    <StatusPill status={i.epistemic} />
                  </article>
                ))}
              </div>
              <h2 style={{ marginTop: 36 }}>Lineage</h2>
              <LineageGraph nodes={view.timeline} />
            </>
          )}
          {tab === "Map" && <p className="muted">Schematic catchment, waterway, and model-domain layers. MOCK extents are not survey-grade.</p>}
          {tab === "Models" &&
            view.models.map((m) => (
              <Link className="result" key={m.id} to={`/model/${m.slug}`}>
                <div className="record-meta">
                  <span>{m.software}</span>
                  <span>{m.dimensionality}</span>
                  <StatusPill status={m.epistemic} />
                </div>
                <h3>{m.name}</h3>
                <p className="muted small">{m.adoptionStatus}</p>
              </Link>
            ))}
          {tab === "Studies" &&
            view.studies.map((s) => (
              <Link className="result" key={s.id} to={`/study/${s.slug}`}>
                <div className="record-meta">
                  <span>{s.publishedAt}</span>
                  <span>{s.software}</span>
                  <StatusPill status={s.epistemic} />
                  <StatusPill status={s.status} />
                </div>
                <h3>{s.title}</h3>
                <p className="muted">{s.summary}</p>
              </Link>
            ))}
          {tab === "Events" &&
            view.events.map((e) => (
              <Link className="result" key={e.id} to={`/event/${e.slug}`}>
                <h3>{e.name}</h3>
                <p className="muted small">{e.startedAt} · {e.summary}</p>
              </Link>
            ))}
          {tab === "Assumptions" && (
            <div className="grid-2">
              {view.assumptions.map((a) => (
                <AssumptionCard key={a.id} a={a} />
              ))}
            </div>
          )}
          {tab === "Decisions" &&
            view.decisions.map((d) => (
              <article className="ev-item" key={d.id}>
                <StatusPill status={d.epistemic} />
                <h3>{d.title}</h3>
                <p>{d.summary}</p>
                <p className="muted small">Depends on {d.modelName ?? "unspecified model"}</p>
              </article>
            ))}
          {tab === "People" && (
            <>
              <p className="muted">
                {h.studyCount} identified public studies · {h.modelCount} model versions · {h.organisations} organisations
                {h.yearSpan ? ` across ${h.yearSpan}` : ""}. There is no modeller ranking.
              </p>
              <table className="table">
                <thead>
                  <tr><th>Organisation</th><th>Type</th></tr>
                </thead>
                <tbody>
                  {view.organisations.map((o) => (
                    <tr key={o.id}><td>{o.name}</td><td>{o.type}</td></tr>
                  ))}
                </tbody>
              </table>
              {view.people.map((p) => (
                <p key={p.id} className="muted small">
                  {p.name} · {p.organisation} · {p.role}
                </p>
              ))}
            </>
          )}
          {tab === "Evidence" &&
            view.evidence.map((e) => (
              <article className="ev-item" key={e.id}>
                <StatusPill status={e.epistemic} />
                <h3>{e.title}</h3>
                <p className="muted small">
                  {e.sourceOrganisation} · {e.publishedAt} · {e.documentType}
                </p>
                <p>{e.excerpt}</p>
                {e.sourceUrl && (
                  <p>
                    <a href={e.sourceUrl} rel="noreferrer">
                      Canonical source
                    </a>
                  </p>
                )}
              </article>
            ))}
          {tab === "Timeline" && <LineageGraph nodes={view.timeline} />}
          {tab === "Knowledge gaps" &&
            view.gaps.map((g) => (
              <article className="card" key={g.id} style={{ marginBottom: 12 }}>
                <StatusPill status={g.synthetic ? "DEMO / SYNTHETIC" : "Unknown / not documented"} />
                <h3>{g.question}</h3>
                <p className="muted small">{g.status} · {g.modelName}</p>
                {g.response ? (
                  <p>
                    {g.response.text} <span className="cite">{g.response.contributor} · {g.response.verificationState}</span>
                  </p>
                ) : (
                  <p className="muted">Unresolved. The assistant will not fabricate an answer.</p>
                )}
              </article>
            ))}
        </div>
      </div>
      <Assistant system={h.slug} />
    </>
  );
}
